"use client";

import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  Flex,
  Image,
  Stack,
  Text,
  Collapse,
  Tooltip,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  Portal,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ChevronDownIcon, ChevronRightIcon, SearchIcon, CloseIcon } from "@chakra-ui/icons";
import { MdHome } from "react-icons/md";
import { mainRoutes } from "common/routes.js";

import Brand from "./Brand";

import SettingIconDark from "common/assets/img/settingIconDark.png";

import { SSORole } from "common/contexts/SSOContext";

import { useSelector } from "react-redux";
import { ROLE } from "common/roles";
import { getActiveRole, getEffectiveRoleName } from "common/utils/roleUtils";

function SidebarContent(props) {
  const navigate = useNavigate();
  const location = useLocation();

  const [showSettingHover, setShowSettingHover] = useState(false);
  const [hoverActive, setHoverActive] = useState(null);
  const [isLogoutScheduled, setIsLogoutScheduled] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [hoveredRoute, setHoveredRoute] = useState(null);
  const [collapsedSubmenu, setCollapsedSubmenu] = useState(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchExpandedCategories, setSearchExpandedCategories] = useState({});

  const userRef = useRef(null);
  const processedRoutesRef = useRef([]);
  const lastSubRouteRef = useRef(null);
  const collapsedSubmenuCloseTimeoutRef = useRef(null);

  const { intervalId } = SSORole();
  const {
    subRoute = [],
    routes = [],
    setOpenSidebar,
    openSidebar,
    isUserLayout,
    from,
    largeLogo,
  } = props;

  const { user: reduxUser } = useSelector((state) => state.user);
  const user = useMemo(() => {
    return reduxUser?.user || reduxUser || JSON.parse(localStorage.getItem("user") || "{}");
  }, [reduxUser]);
  const isUsingPrefilteredRoutes = Array.isArray(subRoute) && subRoute.length > 0;

  useEffect(() => {
    const performanceRoute = Array.isArray(subRoute)
      ? subRoute.find((route) => route?.name === "Performance")
      : null;

    if (performanceRoute) {
      console.log("[Performance][Sidebar] visible-subroutes", {
        performanceSubRoutes: (performanceRoute.subRoutes || []).map((route) => route?.name),
        isUsingPrefilteredRoutes,
      });
    }
  }, [subRoute, isUsingPrefilteredRoutes]);

  userRef.current = user;
  const isEmployee = Array.isArray(user?.roles)
    ? user.roles.some((r) => r?.roleName === ROLE.EMPLOYEE)
    : user?.role === ROLE.EMPLOYEE;

  const getRouteKey = (route) => route?.path || route?.name;

  // Search filter function
  const filterRoutesBySearch = (routes, query) => {
    if (!query.trim()) return routes;

    const lowerQuery = query.toLowerCase().trim();
    const categoriesToExpand = {};

    const filteredRoutes = routes.reduce((acc, route) => {
      const routeNameMatch = route.name?.toLowerCase().includes(lowerQuery);

      // Check if any subroutes match
      if (route.subRoutes && Array.isArray(route.subRoutes)) {
        const matchingSubRoutes = route.subRoutes.filter((subRoute) =>
          subRoute.name?.toLowerCase().includes(lowerQuery)
        );

        if (matchingSubRoutes.length > 0) {
          // Parent has matching children - include parent with filtered children
          categoriesToExpand[route.name] = true;
          acc.push({
            ...route,
            subRoutes: matchingSubRoutes,
          });
          return acc;
        }

        if (routeNameMatch) {
          // Parent name matches - include with all children
          categoriesToExpand[route.name] = true;
          acc.push(route);
          return acc;
        }

        return acc;
      }

      // No subroutes - check if route name matches
      if (routeNameMatch) {
        acc.push(route);
      }

      return acc;
    }, []);

    // Update expanded categories for search results
    setSearchExpandedCategories(categoriesToExpand);

    return filteredRoutes;
  };

  // Memoized filtered routes
  const displayRoutes = useMemo(() => {
    return filterRoutesBySearch(processedRoutesRef.current, searchQuery);
  }, [searchQuery, processedRoutesRef.current]);

  // Highlight matching text
  const highlightMatch = (text, query) => {
    if (!query.trim()) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase().trim();
    const index = lowerText.indexOf(lowerQuery);

    if (index === -1) return text;

    const before = text.slice(0, index);
    const match = text.slice(index, index + query.length);
    const after = text.slice(index + query.length);

    return (
      <>
        {before}
        <Text as="span" bg="yellow.200" color="gray.800" borderRadius="sm" px="1px">
          {match}
        </Text>
        {after}
      </>
    );
  };

  // Get effective expanded state (manual or search-triggered)
  const getEffectiveExpandedState = (categoryKey) => {
    if (searchQuery.trim()) {
      return searchExpandedCategories[categoryKey] || false;
    }
    return expandedCategories[categoryKey] || false;
  };

  // Pre-calculate path to module map for efficient lookup
  const pathToModuleMap = useMemo(() => {
    const map = {};
    const processRoutes = (routeList) => {
      routeList.forEach((route) => {
        if (route.path) {
          const cleanPath = route.path.replace(/^#/, "").split(":")[0].replace(/\/$/, "");
          map[cleanPath] = route.module;
        }
        if (route.subRoutes) {
          processRoutes(route.subRoutes);
        }
      });
    };
    processRoutes(mainRoutes);
    return map;
  }, []);

  const isRouteActiveComprehensive = (currentPath, routePath, routeName, moduleName) => {
    if (!routePath) return false;

    const cleanCurrentPath = currentPath.replace(/^#/, "").split(":")[0].replace(/\/$/, "");
    const cleanRoutePath = routePath.replace(/^#/, "").replace(/\/$/, "");

    // Exact match
    if (cleanCurrentPath === cleanRoutePath) return true;

    // For performance subroutes, use strict exact matching to prevent dual highlighting
    if (cleanRoutePath.startsWith("/performance/")) {
      return cleanCurrentPath === cleanRoutePath;
    }

    // For nested paths, check if it's a child route
    if (cleanCurrentPath.startsWith(cleanRoutePath + "/")) {
      if (cleanRoutePath.endsWith("/reports") && cleanCurrentPath.includes("/reports/scheduled")) {
        return false;
      }
      return true;
    }

    // Module-based matching
    if (moduleName) {
      const currentModule = pathToModuleMap[cleanCurrentPath];
      if (currentModule && currentModule === moduleName) return true;
    }

    const pathSegments = cleanCurrentPath.split("/").filter(Boolean);
    const routeSegments = cleanRoutePath.split("/").filter(Boolean);

    // Module-based matching
    if (moduleName) {
      if (pathToModuleMap[cleanCurrentPath] === moduleName) return true;

      let tempPath = cleanCurrentPath;
      while (tempPath.includes("/")) {
        tempPath = tempPath.substring(0, tempPath.lastIndexOf("/"));
        if (pathToModuleMap[tempPath] === moduleName) return true;
      }
    }

    return false;
  };

  const hasViewAccess = (user, moduleName) => {
    if (!user || !moduleName) return true;

    // Super Admin check
    if (user.role === "superAdmin") return true;

    // 1. Resolve Active Role Name Robustly
    const activeRoleName = getEffectiveRoleName(user);

    // 2. Check if user is an Admin (case-insensitive, checking all possible locations)
    const isAdmin =
      (activeRoleName && ['admin', 'super admin', 'superadmin'].includes(activeRoleName.toLowerCase())) ||
      (user.role && ['admin', 'super admin', 'superadmin'].includes(user.role.toLowerCase())) ||
      (user?.roles && Array.isArray(user.roles) && user.roles.some(r => {
        const rName = (r.roleName || r.role || '').toLowerCase();
        return ['admin', 'super admin', 'superadmin'].includes(rName);
      }));

    // 3. FORCE ALLOW for Admin Users on specific Administration modules
    // This bypasses any DB-stored permissions which might be missing/false for the admin role
    if (isAdmin) {
      return true;
    }

    // 4. Regular Permission Check
    if (!user.roles || !Array.isArray(user.roles)) {
      // Legacy check or simple user object without roles array
      const permissionedModules = [
        "Review Cycles",
        "Review Templates",
        "My Reviews",
        "Document Verification",
        "Onboarding Checklist",
        "Company Policies",
        "Email Templates"
      ];

      if (permissionedModules.includes(moduleName)) {
        return false;
      }
      return true;
    }

    try {
      // Determine which roles to check against
      const activeRole = getActiveRole(user);
      const rolesToCheck = activeRole ? [activeRole] : user.roles;

      for (const role of rolesToCheck) {
        const accessItems = Array.isArray(role.access) ? role.access : [role.access];
        for (const item of accessItems) {
          if (item && item.title === moduleName) {
            // If found, return the view permission
            return item.view === true;
          }
        }
      }

      // Default to false for known permissioned modules if not explicitly found
      const permissionedModules = [
        "Review Cycles",
        "Review Templates",
        "My Reviews",
        "Document Verification",
        "Onboarding Checklist",
        "Company Policies",
        "Email Templates"
      ];

      if (permissionedModules.includes(moduleName)) {
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error checking permissions:", error);
      return true;
    }
  };

  useEffect(() => {
    // Re-run this effect if user role changes effectively
    // ... logic simplified effectively by depending on `user` object changes
    const userRole = getEffectiveRoleName(user) || user?.role;
    const routeSignature = Array.isArray(routes)
      ? routes
          .map((route) => {
            const subRouteSignature = Array.isArray(route?.subRoutes)
              ? route.subRoutes
                  .map((subRoute) => `${subRoute?.name || ""}:${subRoute?.path || ""}`)
                  .join("|")
              : "";

            return `${route?.name || ""}:${route?.path || ""}:${subRouteSignature}`;
          })
          .join("||")
      : "";
    const cacheKey = `${userRole || ""}::${user?._id || ""}::${routeSignature}`;
    if (lastSubRouteRef.current === cacheKey) return;
    lastSubRouteRef.current = cacheKey;

    const sourceRoutes = Array.isArray(subRoute) && subRoute.length > 0 ? subRoute : routes;

    if (!sourceRoutes || !Array.isArray(sourceRoutes) || sourceRoutes.length === 0) {
      processedRoutesRef.current = [];
      return;
    }

    try {
      // Determine if reviewer...
      const userRoleResolved = getEffectiveRoleName(user) || user?.role;
      const isReviewer = userRoleResolved === "reviewer";

      const filteredRoutes = sourceRoutes.reduce((acc, originalRoute) => {
        const route = { ...originalRoute };
        if (route.hidden) return acc;

        const activeRoleName = String(userRoleResolved || user?.role || "").toLowerCase();
        const isAdminUser =
          activeRoleName === "admin" ||
          activeRoleName === "superadmin" ||
          activeRoleName === "super admin" ||
          activeRoleName === "super_admin";

        if (isReviewer) {
          if (route.hidden) return acc;

          // Show My Reviews for reviewers if they have access
          if (route.name === "My Reviews") {
            if (hasViewAccess(user, "My Reviews")) {
              acc.push(route);
            }
            return acc;
          }

          if (route.subRoutes && Array.isArray(route.subRoutes)) {
            const filteredSubRoutes = route.subRoutes.filter((subRoute) => {
              return hasViewAccess(user, subRoute.module);
            });

            if (filteredSubRoutes.length > 0) {
              route.subRoutes = filteredSubRoutes;
              acc.push(route);
            }
            return acc;
          }

          if (route.module && hasViewAccess(user, route.module)) {
            acc.push(route);
          }
          return acc;
        }

        if (route.name === "Dashboard" || route.path === "/dashboard") {
          acc.push(route);
          return acc;
        }

        if (route.isCategory && route.subRoutes && Array.isArray(route.subRoutes)) {
          // Check if ANY subroute has access
          const hasAnySubrouteAccess = route.subRoutes.some((subRoute) => {
            // Use hasViewAccess directly
            if (subRoute.hidden) return false;
            if (subRoute.adminOnly) return isAdminUser;
            return subRoute.module ? hasViewAccess(user, subRoute.module) : true;
          });
          if (hasAnySubrouteAccess) {
            acc.push(route);
          }
          return acc;
        }

        if (route.module && route.module !== null) {
          if (hasViewAccess(user, route.module)) {
            acc.push(route);
          }
          return acc;
        }

        acc.push(route);
        return acc;
      }, []);

      if (!isReviewer) {
        const hasHomeCategory = filteredRoutes.some(
          (route) => route?.isCategory && route?.name === "Home"
        );

        // If Home module is present, avoid duplicate root Dashboard/Todo entries.
        if (hasHomeCategory) {
          for (let i = filteredRoutes.length - 1; i >= 0; i--) {
            const route = filteredRoutes[i];
            if (!route?.isCategory && (route?.name === "Dashboard" || route?.name === "Todo")) {
              filteredRoutes.splice(i, 1);
            }
          }
        } else {
          // Legacy behavior when Home module isn't present.
          const dashboardIndex = filteredRoutes.findIndex(
            (route) => route.name === "Dashboard" || route.path === "/dashboard"
          );

          if (dashboardIndex > 0) {
            const dashboardRoute = filteredRoutes.splice(dashboardIndex, 1)[0];
            filteredRoutes.unshift(dashboardRoute);
          } else if (dashboardIndex === -1) {
            const defaultDashboard = {
              name: "Dashboard",
              path: "/dashboard",
              icon: <Icon as={MdHome} width="20px" height="20px" color="inherit" />,
              component: null,
              module: null,
              order: 1,
            };
            filteredRoutes.unshift(defaultDashboard);
          }
        }
      }

      processedRoutesRef.current = filteredRoutes;

      const currentPath = location.pathname;
      const newExpandedCategories = {};
      const markActiveAncestors = (routeList = []) => {
        return routeList.some((route) => {
          const hasNestedActiveChildren = Array.isArray(route?.subRoutes)
            ? markActiveAncestors(route.subRoutes)
            : false;
          const isRouteDirectlyActive = route?.path
            ? isRouteActiveComprehensive(currentPath, route.path, route.name, route.module)
            : false;
          if (route?.isCategory && route?.subRoutes && (hasNestedActiveChildren || isRouteDirectlyActive)) {
            newExpandedCategories[getRouteKey(route)] = true;
            return true;
          }
          return hasNestedActiveChildren || isRouteDirectlyActive;
        });
      };

      markActiveAncestors(filteredRoutes);

      setExpandedCategories(newExpandedCategories);
    } catch (error) {
      console.error("Error processing routes:", error);
      processedRoutesRef.current = subRoute.filter((route) => !route.hidden) || [];
    }
  }, [routes, location.pathname, user]);

  const logOut = (message) => {
    // ... logic remains same ...
    const rememberedUser = localStorage.getItem("rememberedUser");
    localStorage.clear();
    sessionStorage.clear();
    if (rememberedUser) {
      localStorage.setItem("rememberedUser", rememberedUser);
    }
    if (intervalId) {
      clearInterval(intervalId);
    }
    navigate("/auth/sign-in");
    if (message) {
      toast.error(message);
    } else {
      toast.success("Log out Successfully");
    }
    setIsLogoutScheduled(true);
  };

  const activeRoute = (routePath, routeName = "Unknown", moduleName) => {
    return isRouteActiveComprehensive(location.pathname, routePath, routeName, moduleName);
  };

  const activeSubRoute = (subRoutes = []) => {
    if (!Array.isArray(subRoutes)) return false;

    return subRoutes.some((route) => {
      const isCurrentRouteActive = route?.path
        ? activeRoute(route.path, route.name, route.module)
        : false;

      if (isCurrentRouteActive) {
        return true;
      }

      if (Array.isArray(route?.subRoutes) && route.subRoutes.length > 0) {
        return activeSubRoute(route.subRoutes);
      }

      return false;
    });
  };

  // ... toggleCategory and useEffects remain same ...
  const toggleCategory = (categoryKey) => {
    if (searchQuery.trim()) {
      setSearchExpandedCategories((prev) => ({
        ...prev,
        [categoryKey]: !prev[categoryKey],
      }));
    } else {
      setExpandedCategories((prev) => {
        return {
          ...prev,
          [categoryKey]: !prev[categoryKey],
        };
      });
    }
  };

  const [isBodyOpenSidebar, setIsBodyOpenSidebar] = useState(false);

  useEffect(() => {
    const checkBodyClass = () => {
      setIsBodyOpenSidebar(document.body.classList.contains("openSidebar"));
    };
    checkBodyClass();
    const observer = new MutationObserver(checkBodyClass);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!openSidebar) {
      setSearchQuery("");
      setSearchExpandedCategories({});
    }
  }, [openSidebar]);

  useEffect(() => {
    if (openSidebar) {
      setCollapsedSubmenu(null);
    }
  }, [openSidebar, location.pathname]);

  useEffect(() => {
    return () => {
      if (collapsedSubmenuCloseTimeoutRef.current) {
        clearTimeout(collapsedSubmenuCloseTimeoutRef.current);
      }
    };
  }, []);

  const clearCollapsedSubmenuCloseTimeout = () => {
    if (collapsedSubmenuCloseTimeoutRef.current) {
      clearTimeout(collapsedSubmenuCloseTimeoutRef.current);
      collapsedSubmenuCloseTimeoutRef.current = null;
    }
  };

  const closeCollapsedSubmenu = () => {
    clearCollapsedSubmenuCloseTimeout();
    setHoverActive(null);
    setCollapsedSubmenu(null);
  };

  const scheduleCollapsedSubmenuClose = () => {
    clearCollapsedSubmenuCloseTimeout();
    collapsedSubmenuCloseTimeoutRef.current = setTimeout(() => {
      setHoverActive(null);
      setCollapsedSubmenu(null);
    }, 120);
  };

  const getVisibleSubRoutes = (route) => {
    const subRoutes = route?.subRoutes || [];
    if (isUsingPrefilteredRoutes) {
      return subRoutes;
    }
    const activeRoleName = String(
      user?.activeRoleId?.roleName || user?.role || ""
    ).toLowerCase();
    const isAdminUser =
      activeRoleName === ROLE.admin.toLowerCase() ||
      activeRoleName === ROLE.superAdmin.toLowerCase();

    return subRoutes.filter((subRoute) => {
      if (subRoute.adminOnly) {
        return isAdminUser;
      }
      return hasViewAccess(user, subRoute.module);
    });
  };

  const openCollapsedSubmenu = (route, displayName, triggerElement) => {
    if (!triggerElement || openSidebar || !route?.isCategory) return;

    const visibleSubRoutes = getVisibleSubRoutes(route);
    if (!visibleSubRoutes.length) {
      setCollapsedSubmenu(null);
      return;
    }

    const rect = triggerElement.getBoundingClientRect();
    const submenuWidth = 280;
    const left = Math.min(rect.right - 2, window.innerWidth - submenuWidth - 12);
    clearCollapsedSubmenuCloseTimeout();
    setCollapsedSubmenu({
      routeKey: getRouteKey(route),
      routeName: displayName,
      items: visibleSubRoutes,
      top: Math.max(12, rect.top - 8),
      left,
    });
  };

  const renderCollapsedSubmenuItems = (routes, depth = 0) => {
    return routes.map((subRoute) => {
      const routeKey = getRouteKey(subRoute);
      const hasPath = subRoute.path && subRoute.path !== null;
      const isActive = hasPath
        ? activeRoute(subRoute.path, subRoute.name, subRoute.module)
        : false;
      const visibleNestedSubRoutes = getVisibleSubRoutes(subRoute);
      const hasNestedSubRoutes =
        subRoute.isCategory &&
        Array.isArray(subRoute.subRoutes) &&
        visibleNestedSubRoutes.length > 0;
      const hasActiveNestedSubRoute = hasNestedSubRoutes
        ? activeSubRoute(visibleNestedSubRoutes)
        : false;
      const shouldHighlight = isActive || hasActiveNestedSubRoute;

      return (
        <Box key={`collapsed-submenu-${routeKey}`}>
          <Box
            className="collapsed-submenu-item"
            pl={`${12 + depth * 16}px`}
            pr="12px"
            py="8px"
            borderRadius="8px"
            bg={shouldHighlight ? "rgba(60, 47, 192, 0.08)" : "transparent"}
            color={shouldHighlight ? "#3C2FC0" : "#4A5568"}
            fontSize="13px"
            fontWeight={shouldHighlight ? "600" : "500"}
            lineHeight="1.2"
            cursor={hasPath ? "pointer" : "default"}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            transition="all 0.2s ease"
            _hover={{
              bg: "rgba(60, 47, 192, 0.08)",
              color: "#3C2FC0",
            }}
            onClick={() => {
              if (!hasPath) return;
              navigate(subRoute.path);
              closeCollapsedSubmenu();
            }}
          >
            <Text whiteSpace="nowrap">{subRoute.name}</Text>
            {hasNestedSubRoutes ? (
              <ChevronRightIcon boxSize={3.5} color="currentColor" />
            ) : null}
          </Box>

          {hasNestedSubRoutes ? renderCollapsedSubmenuItems(visibleNestedSubRoutes, depth + 1) : null}
        </Box>
      );
    });
  };

  const renderRouteItem = (route, isSubRoute = false) => {
    const routeKey = getRouteKey(route);
    const hasPath = route.path && route.path !== null;

    const cleanCurrentPath = location.pathname.replace(/^#/, "");
    const cleanRoutePath = hasPath ? route.path.replace(/^#/, "") : "";
    const isActive = hasPath ? activeRoute(route.path, route.name, route.module) : false;
    const hasSubRoutes = route.isCategory && route.subRoutes && route.subRoutes.length > 0;
    const isExpanded = getEffectiveExpandedState(routeKey);
    const isRecruitment = route.name === "Recruitment";
    const flattenRecruitment = isRecruitment && isEmployee;
    const visibleSubRoutes = getVisibleSubRoutes(route);

    const isInterviewsRoute = hasPath && route.path.toLowerCase().includes("/recruitment/interviews");
    const isFeedbackRoute = hasPath && route.path.toLowerCase().includes("/recruitment/feedback");
    const isReferralsRoute = hasPath && route.path.toLowerCase().includes("/recruitment/referrals");
    const displayName = isEmployee
      ? isInterviewsRoute
        ? "My Interviews"
        : isFeedbackRoute
          ? "My Feedbacks"
          : isReferralsRoute
            ? "My Referrals"
            : route.name
      : route.name;
    const hoverKey = route.path || routeKey;
    const hasActiveSubRoute = hasSubRoutes ? activeSubRoute(visibleSubRoutes) : false;
    const shouldHighlight = isActive || hasActiveSubRoute || hoverActive === hoverKey;

    if (flattenRecruitment && !isSubRoute) {
      return (
        <Box key={`${route.name}-flattened`} w="100%" mb={1} position="relative">
          <Box overflow={"visible"} mt={1}>
            {route.subRoutes
              .filter((subRoute) => visibleSubRoutes.includes(subRoute))
              .map((subRoute) => (
                <Box key={subRoute.name}>{renderRouteItem(subRoute, false)}</Box>
              ))}
          </Box>
        </Box>
      );
    }

    return (
      <Box key={routeKey} w="100%" mb={1} position="relative">
        <Tooltip
          label={displayName}
          placement="right"
          openDelay={100}
          closeDelay={50}
          portalProps={{ appendToParentPortal: false }}
          bg="#2D3748"
          color="white"
          borderRadius="6px"
          fontSize="13px"
          px={3}
          py={1}
          isDisabled={isBodyOpenSidebar}
        >
          <Flex
            align="center"
            px={isSubRoute ? 2 : 4}
            mx={isSubRoute ? 2 : 0}
            py={isSubRoute ? 1 : 2}
            my={isSubRoute ? 2 : 0}
            shadow={shouldHighlight ? (hasSubRoutes && !isSubRoute ? "none" : "md") : "none"}
            cursor="pointer"
            borderRadius="lg"
            bg={shouldHighlight && (!hasSubRoutes || isSubRoute) ? "#3C2FC0" : "transparent"}
            color={shouldHighlight ? (hasSubRoutes && !isSubRoute ? "#3C2FC0" : "white") : "#7c7c7c"}
            _hover={{
              bg: "#3C2FC0",
              color: "white",
              shadow: shouldHighlight ? "" : "md",
              transform: "translateX(2px)",
              transition: "all 0.2s",
            }}
            onClick={() => {
              if (flattenRecruitment) return;
              if (hasSubRoutes) {
                if (openSidebar) {
                  toggleCategory(routeKey);
                }
              } else if (hasPath) {
                navigate(route.path);
                setSearchQuery("");
                setSearchExpandedCategories({});
                closeCollapsedSubmenu();
              }
            }}
            fontSize="13px"
            fontWeight="500"
            transition="all 0.2s ease"
            position="relative"
            overflow="visible"
            onMouseEnter={(event) => {
              if (route.icon) setHoveredRoute(route.name);
              if (hasSubRoutes && !openSidebar && !flattenRecruitment) {
                setHoverActive(hoverKey);
                openCollapsedSubmenu(route, displayName, event.currentTarget);
              }
            }}
            onMouseLeave={() => {
              setHoveredRoute(null);
              if (hasSubRoutes && !openSidebar && !flattenRecruitment) {
                scheduleCollapsedSubmenuClose();
              } else {
                setHoverActive(null);
              }
            }}
          >
            {!isSubRoute && hasSubRoutes && shouldHighlight && (
              <Box
                position="absolute"
                left="-12px"
                top={0}
                bottom={0}
                w="6px"
                borderRadius="0 4px 4px 0"
                bg="#3C2FC0"
              />
            )}
            <Box style={{display: "flex",justifyContent: "center",  alignItems: "center", position: "relative", overflow: "visible" }}>
              {route.icon && (
                <Box mr={3} mt={1} opacity={openSidebar ? 1 : 0.8} minW="16px" position="relative">
                  {route.icon}
                </Box>
              )}
            </Box>

            {openSidebar ? (
              <>
                {displayName.length > 15 ?
                  <Tooltip label={displayName} placement="right" isDisabled={!openSidebar} margin={5}>
                    <Text flex="1" isTruncated fontSize="13px" lineHeight="1.2">
                      {displayName}
                    </Text>
                  </Tooltip> :
                  <Text flex="1" isTruncated fontSize="13px" lineHeight="1.2">
                    {displayName}
                  </Text>}
                {hasSubRoutes && !flattenRecruitment && (
                  <Box ml={2} transition="transform 0.2s ease">
                    {isExpanded ? (
                      <ChevronDownIcon className="tab-linkname" w={3} h={3} />
                    ) : (
                      <ChevronRightIcon className="tab-linkname" w={3} h={3} />
                    )}
                  </Box>
                )}
              </>
            ) : null}
          </Flex>
        </Tooltip>

        {hasSubRoutes &&
          openSidebar &&
          (flattenRecruitment ? (
            <Box overflow="visible" mt={1}>
              {route.subRoutes
                .filter((subRoute) => visibleSubRoutes.includes(subRoute))
                .map((subRoute) => (
                  <Box key={subRoute.name}>{renderRouteItem(subRoute, true)}</Box>
                ))}
            </Box>
          ) : (
            <Collapse className="visibleCollapse" overflow="visible" in={isExpanded} animateOpacity>
              <Box
                overflow="visible"
                className="subSidebar"
                mt={1}
                borderLeft="2px solid"
                borderColor="gray.200"
              >
                {route.subRoutes
                  .filter((subRoute) => visibleSubRoutes.includes(subRoute))
                  .map((subRoute) => (
                    <Box key={subRoute.name}>{renderRouteItem(subRoute, true)}</Box>
                  ))}
              </Box>
            </Collapse>
          ))}
      </Box>
    );
  };

  return (
    <Flex direction="column" width="100%" height="100%" borderRadius="30px">
      <Brand
        from={from}
        largeLogo={largeLogo}
        openSidebar={openSidebar}
        setOpenSidebar={setOpenSidebar}
      />

      <Stack
        direction="column"
        mb="auto"
        pt={0}
        className="leftSidebar-container sidebar-overflow top-menu-container"
        spacing={0}
      >
        <Box className="top-menu" pt={0} mt={0}>
          {/* Search Input - Inside top-menu */}
          {openSidebar && (
            <Box mb={2}>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none" h="32px">
                  <SearchIcon color="gray.400" boxSize={3.5} />
                </InputLeftElement>
                <Input
                  placeholder="Search menu..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  borderRadius="6px"
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  h="32px"
                  _placeholder={{ color: "gray.400", fontSize: "13px" }}
                  _hover={{ borderColor: "gray.300" }}
                  _focus={{
                    borderColor: "#3C2FC0",
                    boxShadow: "0 0 0 1px rgba(60, 47, 192, 0.2)",
                    bg: "white",
                  }}
                  fontSize="13px"
                  pr={searchQuery ? "32px" : "12px"}
                />
                {searchQuery && (
                  <InputRightElement h="32px">
                    <IconButton
                      aria-label="Clear search"
                      icon={<CloseIcon boxSize={2} />}
                      size="xs"
                      variant="ghost"
                      borderRadius="full"
                      onClick={() => {
                        setSearchQuery("");
                        setSearchExpandedCategories({});
                      }}
                      _hover={{ bg: "gray.200" }}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
            </Box>
          )}
          {displayRoutes.length > 0 ? (
            displayRoutes.map((route) => renderRouteItem(route))
          ) : searchQuery.trim() ? (
            <Box px={4} py={3}>
              <Text fontSize="13px" color="gray.500" textAlign="center">
                No results found for "{searchQuery}"
              </Text>
            </Box>
          ) : (
            processedRoutesRef.current.map((route) => renderRouteItem(route))
          )}
        </Box>

        <div className="sidebarBottom">
          <div className="sidebarBottom-container">
            <div
              onMouseLeave={() => {
                setShowSettingHover(false);
                setHoverActive(null);
              }}
              onMouseEnter={() => {
                setShowSettingHover(true);
                setHoverActive("settings");
              }}
              onClick={() =>
                navigate(`/userView/${JSON.parse(localStorage.getItem("user"))?._id}`)
              }
              className={`${activeRoute("/userView", "Settings") || hoverActive === "settings"
                ? "sidebarBottom-content activeBox sidebarBottom-border"
                : "sidebarBottom-content sidebarBottom-border"
                }`}
            >
              {activeRoute("/userView", "Settings") && (
                <Box
                  position="absolute"
                  left="-27px"
                  top={0}
                  bottom={0}
                  w="6px"
                  borderRadius="0 4px 4px 0"
                  bg="#3C2FC0"
                />
              )}
              <div className="sidebarBottom-linkContainer">
                <Image
                  objectFit="cover"
                  src={SettingIconDark || "/placeholder.svg"}
                  style={{ opacity: "1" }}
                  h="20px"
                  w="20px"
                />
                {openSidebar && (
                  <Text
                    className={`tab-linkname ${activeRoute("/userView", "Settings") ? "bottomTabActiveLink" : ""
                      }`}
                  >
                    Settings
                  </Text>
                )}
              </div>
              <div
                className="hoverName"
                style={{
                  display: showSettingHover ? "flex" : "none",
                  position: "absolute",
                  left: "30px",
                  backgroundColor: "#2D3748",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  zIndex: 1000,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                }}
              >
                Settings
              </div>
            </div>

          </div>
        </div>
      </Stack>

      {!openSidebar && collapsedSubmenu ? (
        <Portal>
          <Box
            className="collapsed-submenu-popover"
            position="fixed"
            top={`${collapsedSubmenu.top}px`}
            left={`${collapsedSubmenu.left}px`}
            minW="220px"
            maxW="280px"
            bg="white"
            borderRadius="12px"
            boxShadow="0 12px 32px rgba(15, 23, 42, 0.16)"
            border="1px solid"
            borderColor="gray.100"
            p="8px"
            zIndex={1600}
            onMouseEnter={clearCollapsedSubmenuCloseTimeout}
            onMouseLeave={scheduleCollapsedSubmenuClose}
          >
            {renderCollapsedSubmenuItems(collapsedSubmenu.items)}
          </Box>
        </Portal>
      ) : null}
    </Flex>
  );
}

export default SidebarContent;
